import { z } from "zod";
import { paginationMetaSchema } from "./base-api-response";
import { incentiveRuleResponseSchema, IncentiveType, penaltyRuleResponseSchema, PenaltyType, ServiceCategory } from "./earnings-setup";
import { partnerSchema } from "./partner";
import { sessionSchema } from "./session";
import { basePayRuleResponseSchema } from "./earnings-setup";
import { ERewardPenaltyStatusv4 } from "../constants/leave.constants";
import { uuidSchema, partnerIdSchema, yearSchemaBase, yearSchema, yearSchemaRequired, monthSchemaBase, monthSchema, monthSchemaRequired } from "./common";

export enum PartnerEarningSummaryType {
    DAILY = "DAILY",
    WEEKLY = "WEEKLY",
    MONTHLY = "MONTHLY",
    QUARTERLY = "QUARTERLY",
    YEARLY = "YEARLY",
}


export const paginationDtoSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(10),
});

export const searchMonthlyEarningsRequestDtoSchema = paginationDtoSchema.extend({
    partnerId: z.string().nullish(),
    year: yearSchema(),
    month: monthSchema(),
    yearFrom: yearSchema(),
    yearTo: yearSchema(),
    isFinalized: z.preprocess(val => {
        if (val === "true" || val === true) return true;
        if (val === "false" || val === false) return false;
        return undefined;
    }, z.boolean().nullish()),
    query: z.string().trim().min(1).nullish(),
    serviceCategory: z.nativeEnum(ServiceCategory).nullish(),
});

export type SearchMonthlyEarningsRequestDto = z.infer<typeof searchMonthlyEarningsRequestDtoSchema>;

export const monthlyEarningResponseSchema = z.object({
    id: z.string().uuid(),
    partner: partnerSchema,
    uniqueCode: z.string(),
    month: monthSchemaBase.nullish(),
    year: yearSchemaBase.nullish(),
    summaryType: z.nativeEnum(PartnerEarningSummaryType),
    totalBasePay: z.number(),
    totalIncentives: z.number(),
    totalPenalties: z.number(),
    totalAdjustments: z.number(),
    netEarning: z.number(),
    totalSessions: z.number(),
    isFinalized: z.boolean(),
    calculatedAt: z.number(),
    createdAt: z.number(),
    updatedAt: z.number(),
});

export type MonthlyEarningResponseDto = z.infer<typeof monthlyEarningResponseSchema>;

export const monthlyEarningsListResponseSchema = z.object({
    items: z.array(monthlyEarningResponseSchema),
    meta: paginationMetaSchema,
});

export type IMonthlyEarningsListResponse = z.infer<typeof monthlyEarningsListResponseSchema>;


export const basePaySummaryRequestDtoSchema = z.object({
    partnerId: partnerIdSchema,
    year: yearSchemaRequired(),
    month: monthSchemaRequired(),
});

export type BasePaySummaryRequestDto = z.infer<typeof basePaySummaryRequestDtoSchema>;

export const basePaySummaryResponseSchema = z.object({
    partner: partnerSchema,
    month: monthSchemaBase,
    year: yearSchemaBase,
    totalSessions: z.number().int().min(0),
    totalEarning: z.number(),
});

export type BasePaySummaryResponseDto = z.infer<typeof basePaySummaryResponseSchema>;

export enum RewardPenaltyStatus {
    PENDING = "PENDING",
    APPROVED = "APPROVED",
    REJECTED = "REJECTED",
}

export const basePayRecordResponseSchema = z.object({
    id: uuidSchema(),
    partner: partnerSchema,
    session: sessionSchema,
    rule: basePayRuleResponseSchema,
    amount: z.number(),
    status: z.nativeEnum(RewardPenaltyStatus),
    calculatedAt: z.number(),
    createdAt: z.number(),
    updatedAt: z.number(),
    isPaid: z.boolean(),
    paidAt: z.number().nullish(),
});

export type BasePayRecordResponseDto = z.infer<typeof basePayRecordResponseSchema>;

export const searchBasePayRecordsRequestDtoSchema = paginationDtoSchema.extend({
    partnerId: partnerIdSchema,
    year: yearSchema(),
    month: monthSchema(),
});

export type SearchBasePayRecordsRequestDto = z.infer<typeof searchBasePayRecordsRequestDtoSchema>;

export const basePayRecordsListResponseSchema = z.object({
    items: z.array(basePayRecordResponseSchema),
    meta: paginationMetaSchema,
});

export type IBasePayRecordsListResponse = z.infer<typeof basePayRecordsListResponseSchema>;

export const incentiveSummaryRequestDtoSchema = z.object({
    partnerId: partnerIdSchema,
    year: yearSchemaRequired(),
    month: monthSchemaRequired(),
});

export type IncentiveSummaryRequestDto = z.infer<typeof incentiveSummaryRequestDtoSchema>;

export const incentiveRecordSchema = z.object({
    id: uuidSchema(),
    session: sessionSchema.nullish(),
    amount: z.number(),
    calculatedAt: z.number(),
    // Present for aggregate (weekly/monthly) incentives, absent for session-based ones
    periodStartsAt: z.number().nullish(),
    periodEndsAt: z.number().nullish(),
    status: z.nativeEnum(ERewardPenaltyStatusv4).nullish(),
    rule: incentiveRuleResponseSchema,
    evaluationMetrics: z.record(z.unknown()).nullish(),
});

export type IncentiveRecordDto = z.infer<typeof incentiveRecordSchema>;

export const incentiveGroupSchema = z.object({
    type: z.nativeEnum(IncentiveType),
    typeName: z.string(),
    totalAmount: z.number(),
    count: z.number(),
    records: z.array(incentiveRecordSchema),
});

export type IncentiveGroupDto = z.infer<typeof incentiveGroupSchema>;

export const incentiveSummaryResponseSchema = z.object({
    partnerId: uuidSchema(),
    month: monthSchemaBase,
    year: yearSchemaBase,
    totalIncentives: z.number(),
    groups: z.array(incentiveGroupSchema),
});

export type IncentiveSummaryResponseDto = z.infer<typeof incentiveSummaryResponseSchema>;

// ============================================================================
// PENALTY SUMMARY INTERFACES
// ============================================================================

export const penaltySummaryRequestDtoSchema = z.object({
    partnerId: partnerIdSchema,
    year: yearSchemaRequired(),
    month: monthSchemaRequired(),
});

export type PenaltySummaryRequestDto = z.infer<typeof penaltySummaryRequestDtoSchema>;

export const penaltyRecordSchema = z.object({
    id: uuidSchema(),
    session: sessionSchema.nullish(),
    amount: z.number(),
    calculatedAt: z.number(),
    rule: penaltyRuleResponseSchema,
    evaluationMetrics: z.record(z.unknown()).nullish(),
});

export type PenaltyRecordDto = z.infer<typeof penaltyRecordSchema>;

export const penaltyGroupSchema = z.object({
    type: z.nativeEnum(PenaltyType),
    typeName: z.string(),
    totalAmount: z.number(),
    count: z.number(),
    records: z.array(penaltyRecordSchema),
});

export type PenaltyGroupDto = z.infer<typeof penaltyGroupSchema>;

export const penaltySummaryResponseSchema = z.object({
    partnerId: uuidSchema(),
    month: monthSchemaBase,
    year: yearSchemaBase,
    totalPenalties: z.number(),
    groups: z.array(penaltyGroupSchema),
});

export type PenaltySummaryResponseDto = z.infer<typeof penaltySummaryResponseSchema>;

// ============================================================================
// ADJUSTMENT INTERFACES
// ============================================================================

export enum AdjustmentDirection {
    CREDIT = "CREDIT",
    DEBIT = "DEBIT",
}

export enum AdjustmentReferenceType {
    SESSION = "SESSION",
    LOAN_INSTALLMENT = "LOAN_INSTALLMENT",
    LOAN_CREDIT = "LOAN_CREDIT",
    PENDING_COMMISSION = "PENDING_COMMISSION",
    SECURITY_DEPOSIT = "SECURITY_DEPOSIT",
    BASE_PAY_ADDITION = "BASE_PAY_ADDITION",
    BASE_PAY_REVERSAL = "BASE_PAY_REVERSAL",
    INCENTIVE_ADDITION = "INCENTIVE_ADDITION",
    INCENTIVE_REVERSAL = "INCENTIVE_REVERSAL",
    PENALTY_ADDITION = "PENALTY_ADDITION",
    PENALTY_REVERSAL = "PENALTY_REVERSAL",
    OTHER_ADDITION = "OTHER_ADDITION",
    CANCELLATION_ADJUSTMENT = "CANCELLATION_ADJUSTMENT",
    OTHER_REVERSAL = "OTHER_REVERSAL",
    CASH_COLLECTED_SESSION = "CASH_COLLECTED_SESSION",
    BONUS_ADDITION = "BONUS_ADDITION",
    MANUAL = "MANUAL",
    OTHER = "OTHER",
}

export enum CalculationBasis {
    SESSION = "SESSION",
    AGGREGATE_WEEKLY = "AGGREGATE_WEEKLY",
    AGGREGATE_MONTHLY = "AGGREGATE_MONTHLY",
    AGGREGATE_CUSTOM = "AGGREGATE_CUSTOM",
}

export const createAdjustmentRequestDtoSchema = z.object({
    partnerId: partnerIdSchema,
    sessionId: z.string().nullish(),
    basis: z.nativeEnum(CalculationBasis),
    periodStartsAt: z.string().regex(/^\d{2}-\d{2}-\d{4}$/).nullish(),
    periodEndsAt: z.string().regex(/^\d{2}-\d{2}-\d{4}$/).nullish(),
    adjustmentDirection: z.nativeEnum(AdjustmentDirection),
    amount: z.number().positive(),
    referenceType: z.nativeEnum(AdjustmentReferenceType).optional(),
    referenceId: uuidSchema().nullish(),
    parentAdjustmentId: uuidSchema().nullish(),
    description: z.string().min(1),
    internalNotes: z.string().optional(),
});

export type CreateAdjustmentRequestDto = z.infer<typeof createAdjustmentRequestDtoSchema>;

export const adjustmentResponseDtoSchema = z.object({
    id: z.string().uuid(),
    partner: partnerSchema.nullish(),
    uniqueCode: z.string(),
    amount: z.number(),
    adjustmentDirection: z.nativeEnum(AdjustmentDirection),
    status: z.nativeEnum(RewardPenaltyStatus),
    description: z.string().nullish(),
    notes: z.string().nullish(),
    basis: z.nativeEnum(CalculationBasis).nullish(),
    referenceType: z.nativeEnum(AdjustmentReferenceType).nullish(),
    referenceId: z.string().uuid().nullish(),
    parentAdjustmentId: z.string().uuid().nullish(),
    calculatedAt: z.number(),
    periodStartsAt: z.number().nullish(),
    periodEndsAt: z.number().nullish(),
    requestedBy: z.string().nullish(),
    approvedBy: z.string().nullish(),
    approvedAt: z.number().nullish(),
    approvalComments: z.string().nullish(),
    rejectedBy: z.string().nullish(),
    rejectedAt: z.number().nullish(),
    rejectionReason: z.string().nullish(),
    isReversed: z.boolean().nullish(),
    reversedBy: z.string().nullish(),
    reversedAt: z.number().nullish(),
    reversalReason: z.string().nullish(),
    isPaid: z.boolean().nullish(),
    payoutId: z.string().uuid().nullish(),
    paidAt: z.number().nullish(),
    createdBy: z.string(),
    createdAt: z.number(),
    updatedBy: z.string(),
    updatedAt: z.number(),
    session: sessionSchema.nullish(),
    penalty: penaltyRecordSchema.nullish(),
});

export type AdjustmentResponseDto = z.infer<typeof adjustmentResponseDtoSchema>;

export const searchAdjustmentsRequestDtoSchema = paginationDtoSchema.extend({
    partnerId: partnerIdSchema.nullish(),
    status: z.nativeEnum(RewardPenaltyStatus).optional(),
    adjustmentDirection: z.nativeEnum(AdjustmentDirection).optional(),
    referenceType: z.nativeEnum(AdjustmentReferenceType).optional(),
    referenceId: uuidSchema().optional(),
    calculatedAtFrom: z.preprocess(val => (val ? Number(val) : undefined), z.number().int().positive().nullish()),
    calculatedAtTo: z.preprocess(val => (val ? Number(val) : undefined), z.number().int().positive().nullish()),
    search: z.string().optional(),
    isPaid: z.boolean().optional(),
    isReversed: z.boolean().optional(),
});

export type SearchAdjustmentsRequestDto = z.infer<typeof searchAdjustmentsRequestDtoSchema>;

export const adjustmentsListResponseSchema = z.object({
    items: z.array(adjustmentResponseDtoSchema),
    meta: paginationMetaSchema,
});

export type IAdjustmentsListResponse = z.infer<typeof adjustmentsListResponseSchema>;

// ============================================================================
// ADJUSTMENT FORM SCHEMA
// ============================================================================

export const adjustmentFormSchema = z.object({
    adjustmentType: z.nativeEnum(AdjustmentReferenceType, {
        required_error: "Adjustment type is required",
    }).nullish(),
    sessionId: z.string().optional(),
    penaltyId: z.string().optional(),
    incentiveId: z.string().optional(),
    basePayId: z.string().optional(),
    amount: z.string().min(1, "Amount is required").refine((val) => {
        const num = Number(val);
        return !isNaN(num) && num > 0;
    }, "Amount must be a positive number"),
    reason: z.string().min(1, "Reason is required"),
}).refine((data) => {
    if (!data.adjustmentType) {
        return false;
    }
    return true;
}, {
    message: "Adjustment type is required",
    path: ["adjustmentType"],
}).refine((data) => {
    // For additions: require sessionId
    if (data.adjustmentType === AdjustmentReferenceType.PENALTY_ADDITION ||
        data.adjustmentType === AdjustmentReferenceType.INCENTIVE_ADDITION ||
        data.adjustmentType === AdjustmentReferenceType.BASE_PAY_ADDITION ||
        data.adjustmentType === AdjustmentReferenceType.CANCELLATION_ADJUSTMENT ||
        data.adjustmentType === AdjustmentReferenceType.CASH_COLLECTED_SESSION) {
        return data.sessionId && data.sessionId.length > 0;
    }
    return true;
}, {
    message: "Session ID is required",
    path: ["sessionId"],
}).refine((data) => {
    // For penalty reversal: require penaltyId
    if (data.adjustmentType === AdjustmentReferenceType.PENALTY_REVERSAL) {
        return data.penaltyId && data.penaltyId.length > 0;
    }
    return true;
}, {
    message: "Penalty ID is required for penalty reversal",
    path: ["penaltyId"],
}).refine((data) => {
    // For incentive reversal: require incentiveId
    if (data.adjustmentType === AdjustmentReferenceType.INCENTIVE_REVERSAL) {
        return data.incentiveId && data.incentiveId.length > 0;
    }
    return true;
}, {
    message: "Incentive ID is required for incentive reversal",
    path: ["incentiveId"],
}).refine((data) => {
    // For base pay reversal: require basePayId
    if (data.adjustmentType === AdjustmentReferenceType.BASE_PAY_REVERSAL) {
        return data.basePayId && data.basePayId.length > 0;
    }
    return true;
}, {
    message: "Base Pay ID is required for base pay reversal",
    path: ["basePayId"],
});

export type AdjustmentFormValues = z.infer<typeof adjustmentFormSchema>;

